import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { SessionInfo, AgentState } from '../api/types';
import { api } from '../api/client';
import { useInstanceMode } from '../hooks/useInstanceMode';
import './AgentInfoModal.css';

interface Props {
  session: SessionInfo;
  state?: AgentState;
  mcpCount?: number;
  onClose: () => void;
}

function formatTimestamp(ts: number | string | undefined): string {
  if (!ts) return '—';
  const n = typeof ts === 'string' ? parseFloat(ts) : ts;
  if (!Number.isFinite(n) || n <= 0) return '—';
  const d = new Date(n * 1000);
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatRelative(ts: number | undefined): string {
  if (!ts || ts <= 0) return '—';
  const diff = Date.now() / 1000 - ts;
  if (diff < 60) return 'gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86_400) return `vor ${Math.floor(diff / 3600)}h`;
  return `vor ${Math.floor(diff / 86_400)} Tagen`;
}

interface Row {
  label: string;
  value: React.ReactNode;
}

export function AgentInfoModal({ session, state, mcpCount, onClose }: Props) {
  const instanceMode = useInstanceMode();
  const [mcpList, setMcpList] = useState<string[] | null>(null);

  useEffect(() => {
    api.sessions.getMcp(session.name)
      .then((d) => setMcpList(Object.keys(d.servers || {}).sort()))
      .catch(() => setMcpList([]));
  }, [session.name]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const identity: Row[] = [
    { label: 'Name', value: session.name },
    { label: 'Display', value: session.display_name || <span className="ai-muted">—</span> },
    ...(instanceMode === 'federation' ? [{ label: 'Node', value: session.node || 'local' } as Row] : []),
    { label: 'Window', value: <span className="ai-mono">{session.window_name}</span> },
  ];

  const rbac: Row[] = [
    { label: 'Owner', value: session.owner_email || session.owner || <span className="ai-muted">—</span> },
    { label: 'Department', value: session.department || <span className="ai-muted">—</span> },
    { label: 'Role', value: session.role || <span className="ai-muted">—</span> },
  ];

  const runtime: Row[] = [
    { label: 'Agent Type', value: <span className="ai-mono">{session.agent || '—'}</span> },
    { label: 'Model', value: <span className="ai-mono">{session.model || '—'}</span> },
    { label: 'State', value: <span className="ai-mono">{state?.state || 'unknown'}{state?.detail ? ` · ${state.detail}` : ''}</span> },
    { label: 'MCP Servers', value: mcpCount !== undefined ? mcpCount : (mcpList?.length ?? '—') },
  ];

  const lifecycle: Row[] = [
    { label: 'Created', value: <span title={session.created}>{formatTimestamp(session.created)}</span> },
    { label: 'Last activity', value: <span title={formatTimestamp(session.last_activity)}>{formatRelative(session.last_activity)}</span> },
    { label: 'Last alive', value: formatRelative(session.last_seen_alive) },
    { label: 'Last health check', value: formatRelative(session.last_health_check_at) },
    { label: 'Must be online', value: session.must_be_online ? 'yes' : 'no' },
    { label: 'Auto restart', value: session.auto_restart ? 'yes' : 'no' },
  ];

  const sections: { title: string; rows: Row[] }[] = [
    { title: 'Identity', rows: identity },
    { title: 'RBAC', rows: rbac },
    { title: 'Runtime', rows: runtime },
    { title: 'Lifecycle', rows: lifecycle },
  ];

  return (
    <>
      <div className="ai-backdrop" onClick={onClose} />
      <div className="ai-modal" role="dialog" aria-label={`Agent Info ${session.name}`}>
        <header className="ai-header">
          <div className="ai-title">{session.name}</div>
          <button type="button" className="ai-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="ai-body">
          {sections.map((sec) => (
            <section key={sec.title} className="ai-section">
              <h3 className="ai-section-title">{sec.title}</h3>
              <dl className="ai-rows">
                {sec.rows.map((r) => (
                  <div key={r.label} className="ai-row">
                    <dt className="ai-label">{r.label}</dt>
                    <dd className="ai-value">{r.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}

          {mcpList !== null && mcpList.length > 0 && (
            <section className="ai-section">
              <h3 className="ai-section-title">MCP Servers</h3>
              <div className="ai-mcp-grid">
                {mcpList.map((s) => (
                  <span key={s} className="ai-mcp-pill">{s}</span>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
